import type { HTMLAttributes, ReactNode } from "react";
import { Icon, type IconName } from "./icons";

// The five screen states — codified from 12-durumlar-geri-bildirim.html: empty,
// loading (spinner + shimmer), error, success, and restricted-access. Each is a calm,
// self-contained surface; copy + actions are the caller's. Empty is a beginning, not a
// failure; restricted is a trust signal (role="note"), not an error.

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  icon?: IconName;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ icon, title, description, action, ...rest }: EmptyStateProps) {
  return (
    <div className="k-empty" role="status" {...rest}>
      {icon ? (
        <div className="k-empty__icon">
          <Icon name={icon} size={26} strokeWidth={1.6} />
        </div>
      ) : null}
      <div className="k-empty__title">{title}</div>
      {description ? <div className="k-empty__desc">{description}</div> : null}
      {action}
    </div>
  );
}

/** The shimmer rows (avatar + two lines) — reusable placeholder content. */
export function Skeleton({ rows = 2 }: { rows?: number }) {
  return (
    <div className="k-shimmer-rows" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div className="k-shimmer-row" key={i}>
          <div className="k-shimmer k-shimmer--avatar" />
          <div style={{ flex: "1 1 0" }}>
            <div className="k-shimmer k-shimmer--line" style={{ width: "52%" }} />
            <div className="k-shimmer k-shimmer--line-sm" style={{ width: "34%" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export interface LoadingStateProps extends HTMLAttributes<HTMLDivElement> {
  label: ReactNode;
  rows?: number;
}

export function LoadingState({ label, rows = 2, ...rest }: LoadingStateProps) {
  return (
    <div className="k-loading" role="status" aria-live="polite" {...rest}>
      <div className="k-loading__head">
        <span className="k-loading__spinner" aria-hidden="true" />
        <span className="k-loading__label">{label}</span>
      </div>
      <Skeleton rows={rows} />
    </div>
  );
}

export interface ErrorStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title: ReactNode;
  description?: ReactNode;
  /** Optional actions row (pass kit Buttons). */
  actions?: ReactNode;
}

export function ErrorState({ title, description, actions, ...rest }: ErrorStateProps) {
  return (
    <div className="k-note k-note--error" role="alert" {...rest}>
      <div className="k-note__row">
        <div className="k-note__icon k-note__icon--error">
          <Icon name="alert-triangle" size={21} strokeWidth={1.9} />
        </div>
        <div style={{ flex: "1 1 0" }}>
          <div className="k-note__title">{title}</div>
          {description ? <div className="k-note__desc">{description}</div> : null}
          {actions ? <div className="k-note__actions">{actions}</div> : null}
        </div>
      </div>
    </div>
  );
}

export interface SuccessStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title: ReactNode;
  description?: ReactNode;
}

export function SuccessState({ title, description, ...rest }: SuccessStateProps) {
  return (
    <div className="k-note" role="status" {...rest}>
      <div className="k-note__row" style={{ alignItems: "center" }}>
        <div className="k-note__icon k-note__icon--success">
          <Icon name="check-circle" size={21} strokeWidth={2} />
        </div>
        <div>
          <div className="k-note__title" style={{ marginBottom: 0 }}>
            {title}
          </div>
          {description ? <div className="k-note__desc">{description}</div> : null}
        </div>
      </div>
    </div>
  );
}

export interface RestrictedStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title: ReactNode;
  description?: ReactNode;
}

export function RestrictedState({ title, description, ...rest }: RestrictedStateProps) {
  return (
    <div className="k-note" role="note" {...rest}>
      <div className="k-note__row" style={{ gap: 13 }}>
        <div className="k-note__icon k-note__icon--restricted">
          <Icon name="lock" size={17} strokeWidth={1.9} />
        </div>
        <div>
          <div className="k-note__title k-note__title--sm">{title}</div>
          {description ? <div className="k-note__desc">{description}</div> : null}
        </div>
      </div>
    </div>
  );
}
